/**
 * Email Provider Emulation Plugin
 *
 * Emulates the HTTP send APIs of several email providers so a sandboxed app's
 * outbound mail is captured in-process instead of leaving the sandbox. One
 * plugin spans many providers; each send request normalizes into a shared
 * `CapturedEmail` shape for assertions and API-side inbox ingestion.
 *
 * Supported providers (HTTP APIs only):
 * - Amazon SES: `POST /` and SES v2 `POST /v2/email/outbound-emails`.
 * - SendGrid: `POST /v3/mail/send`.
 * - Resend: `POST /emails`.
 * - Postmark: `POST /email` and `POST /email/batch`.
 * - Mailgun: `POST /v3/:domain/messages`.
 *
 * Unknown paths on these hosts pass through to the real provider.
 *
 * @module
 */

import type { EmulationPlugin } from '../plugin.js';
import { emailActionInterfaces } from './email/actions.js';
import {
  handleMailgun,
  handlePostmarkBatch,
  handlePostmarkSingle,
  handleResend,
  handleSendgrid,
  handleSesV1,
  handleSesV2,
} from './email/handlers.js';
import { emailFixtureSchema } from './email/types.js';
import type { EmailCtx, EmailEmuConfig, EmailFixture, EmailState } from './email/types.js';

export { emailFixtureSchema } from './email/types.js';
export type {
  CapturedEmail,
  EmailEmuConfig,
  EmailFixture,
  EmailProvider,
  EmailState,
} from './email/types.js';

const DEFAULT_SES_REGIONS = ['us-east-1'];

export const emailPlugin: EmulationPlugin<EmailEmuConfig, EmailFixture, EmailState> = {
  id: 'email',
  name: 'Email (multi-provider)',
  version: '1.0.0',

  domains: (config) => {
    const sesRegions = config.sesRegions?.length ? config.sesRegions : DEFAULT_SES_REGIONS;
    return [
      'api.sendgrid.com',
      'api.resend.com',
      'api.postmarkapp.com',
      'api.mailgun.net',
      'api.eu.mailgun.net',
      ...sesRegions.map((region) => `email.${region}.amazonaws.com`),
      ...(config.extraDomains ?? []),
    ];
  },

  needsBrowserIntercept: false,

  fixtureSchema: emailFixtureSchema,
  defaultFixture: {},

  actionInterfaces: emailActionInterfaces,

  controlReads: [
    {
      path: 'sent-emails',
      handler: (_req, ctx: EmailCtx) => ({
        status: 200,
        body: { emails: ctx.state.sentEmails },
      }),
    },
  ],

  routes: [
    { method: 'POST', path: '/v3/mail/send', handler: handleSendgrid },
    { method: 'POST', path: '/emails', handler: handleResend },
    { method: 'POST', path: '/email', handler: handlePostmarkSingle },
    { method: 'POST', path: '/email/batch', handler: handlePostmarkBatch },
    { method: 'POST', path: '/v3/:domain/messages', handler: handleMailgun },
    { method: 'POST', path: '/v2/email/outbound-emails', handler: handleSesV2 },
    { method: 'POST', path: '/', handler: handleSesV1 },
  ],

  initialize(_config, _fixture, _env): EmailState {
    return { sentEmails: [] };
  },
};
